/**
 * 简单的查询字符串序列化工具
 * 类似于 qs.stringify 的功能
 */

export interface QsOptions {
  arrayFormat?: 'indices' | 'brackets' | 'repeat' | 'comma';
  encode?: boolean;
  skipNulls?: boolean;
}

/**
 * 将对象序列化为查询字符串
 * @param obj 要序列化的对象
 * @param options 序列化选项
 * @returns 查询字符串
 */
export function stringify(obj: Record<string, any>, options: QsOptions = {}): string {
  const { arrayFormat = 'indices', encode = true, skipNulls = false } = options;

  const pairs: string[] = [];

  function addPair(key: string, value: any, prefix = '') {
    if (value === null || value === undefined) {
      if (!skipNulls) {
        pairs.push(`${prefix}${key}=`);
      }
      return;
    }

    if (Array.isArray(value)) {
      if (value.length === 0) {
        pairs.push(`${prefix}${key}=`);
        return;
      }

      switch (arrayFormat) {
        case 'indices':
          value.forEach((item, index) => {
            addPair(`${key}[${index}]`, item, prefix);
          });
          break;
        case 'brackets':
          value.forEach((item) => {
            addPair(`${key}[]`, item, prefix);
          });
          break;
        case 'repeat':
          value.forEach((item) => {
            addPair(key, item, prefix);
          });
          break;
        case 'comma':
          pairs.push(`${prefix}${key}=${value.join(',')}`);
          break;
      }
      return;
    }

    if (typeof value === 'object') {
      Object.keys(value).forEach((subKey) => {
        addPair(`${key}[${subKey}]`, value[subKey], prefix);
      });
      return;
    }

    const encodedValue = encode ? encodeURIComponent(String(value)) : String(value);
    pairs.push(`${prefix}${key}=${encodedValue}`);
  }

  Object.keys(obj).forEach((key) => {
    addPair(key, obj[key]);
  });

  return pairs.join('&');
}

/**
 * 将查询字符串解析为对象
 * @param str 查询字符串
 * @returns 解析后的对象
 */
export function parse(str: string): Record<string, any> {
  if (!str) return {};

  const obj: Record<string, any> = {};
  const pairs = str.split('&');

  pairs.forEach((pair) => {
    const [key, value] = pair.split('=');
    if (key) {
      const decodedValue = value ? decodeURIComponent(value) : '';

      // 处理数组索引格式 key[0], key[1]
      if (key.includes('[') && key.includes(']')) {
        const match = key.match(/^([^[]+)\[(\d+)\]$/);
        if (match) {
          const [, arrayKey, index] = match;
          if (!obj[arrayKey]) obj[arrayKey] = [];
          obj[arrayKey][parseInt(index)] = decodedValue;
          return;
        }
      }

      // 处理对象格式 key[subkey]
      if (key.includes('[') && key.includes(']')) {
        const match = key.match(/^([^[]+)\[([^\]]+)\]$/);
        if (match) {
          const [, objKey, subKey] = match;
          if (!obj[objKey]) obj[objKey] = {};
          obj[objKey][subKey] = decodedValue;
          return;
        }
      }

      // 处理普通键值对
      obj[key] = decodedValue;
    }
  });

  return obj;
}

export default {
  stringify,
  parse,
};
